import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Modal,
	Form,
	Input,
	Select,
	Button,
	Space,
	Card,
	Typography,
	message,
	Divider,
	Row,
	Col,
	Tag,
	Spin,
} from 'antd';
import {
	PlusOutlined,
	DeleteOutlined,
	PlayCircleOutlined,
} from '@ant-design/icons';
import { CALL_SNAPPY_API } from './RewardsStoreGraphQL';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SnappyApiTestModal = ({ open, onCancel, client }) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState(null);
	const [queryParams, setQueryParams] = useState([{ key: '', value: '' }]);

	const handleAddQueryParam = () => {
		setQueryParams([...queryParams, { key: '', value: '' }]);
	};

	const handleRemoveQueryParam = (index) => {
		const newParams = queryParams.filter((_, i) => i !== index);
		setQueryParams(newParams);
	};

	const handleQueryParamChange = (index, field, value) => {
		const newParams = [...queryParams];
		newParams[index][field] = value;
		setQueryParams(newParams);
	};

	const handleSubmit = async (values) => {
		try {
			setLoading(true);
			setResult(null);

			// Filter out empty query params
			const filteredQueryParams = queryParams.filter(
				(param) => param.key.trim() !== '' && param.value.trim() !== ''
			);

			const variables = {
				companyId: values.companyId || currentUser?.company?.id,
				endpoint: values.endpoint,
				queryParams:
					filteredQueryParams.length > 0 ? filteredQueryParams : null,
				method: values.method || 'GET',
				stringifiedBody: values.stringifiedBody || null,
			};

			console.log('Calling CALL_SNAPPY_API with variables:', variables);

			const { data } = await client.query({
				query: CALL_SNAPPY_API,
				variables,
				fetchPolicy: 'network-only',
			});

			setResult({
				success: true,
				data: data?.callSnappyApi,
				variables,
			});

			message.success('API call completed successfully!');
		} catch (error) {
			console.error('Error calling Snappy API:', error);
			setResult({
				success: false,
				error: error.message,
				variables: {
					companyId: values.companyId || currentUser?.company?.id,
					endpoint: values.endpoint,
					queryParams: queryParams.filter(
						(param) => param.key.trim() !== '' && param.value.trim() !== ''
					),
					method: values.method || 'GET',
					stringifiedBody: values.stringifiedBody || null,
				},
			});
			message.error('API call failed. Check the result below for details.');
		} finally {
			setLoading(false);
		}
	};

	const handleReset = () => {
		form.resetFields();
		setQueryParams([{ key: '', value: '' }]);
		setResult(null);
	};

	const handleModalCancel = () => {
		handleReset();
		onCancel();
	};

	return (
		<Modal
			title="Snappy API Test Tool"
			open={open}
			onCancel={handleModalCancel}
			footer={null}
			width={800}
			destroyOnClose
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={{
					companyId: currentUser?.company?.id,
					method: 'GET',
				}}
			>
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							name="companyId"
							label="Company ID"
							rules={[{ required: true, message: 'Please enter company ID' }]}
						>
							<Input placeholder="Company ID" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name="method"
							label="HTTP Method"
							rules={[{ required: true, message: 'Please select method' }]}
						>
							<Select>
								<Option value="GET">GET</Option>
								<Option value="POST">POST</Option>
								<Option value="PUT">PUT</Option>
								<Option value="DELETE">DELETE</Option>
								<Option value="PATCH">PATCH</Option>
							</Select>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item
					name="endpoint"
					label="API Endpoint"
					rules={[{ required: true, message: 'Please enter API endpoint' }]}
				>
					<Input placeholder="e.g., /campaigns, /gifts, /users" />
				</Form.Item>

				<Divider>Query Parameters</Divider>
				<div style={{ marginBottom: 16 }}>
					{queryParams.map((param, index) => (
						<Row key={index} gutter={8} style={{ marginBottom: 8 }}>
							<Col span={10}>
								<Input
									placeholder="Parameter key"
									value={param.key}
									onChange={(e) =>
										handleQueryParamChange(index, 'key', e.target.value)
									}
								/>
							</Col>
							<Col span={10}>
								<Input
									placeholder="Parameter value"
									value={param.value}
									onChange={(e) =>
										handleQueryParamChange(index, 'value', e.target.value)
									}
								/>
							</Col>
							<Col span={4}>
								<Space>
									{index === queryParams.length - 1 && (
										<Button
											type="dashed"
											icon={<PlusOutlined />}
											onClick={handleAddQueryParam}
											size="small"
										/>
									)}
									{queryParams.length > 1 && (
										<Button
											danger
											icon={<DeleteOutlined />}
											onClick={() => handleRemoveQueryParam(index)}
											size="small"
										/>
									)}
								</Space>
							</Col>
						</Row>
					))}
				</div>

				<Form.Item name="stringifiedBody" label="Request Body (JSON string)">
					<TextArea
						rows={4}
						placeholder='{"key": "value"} - Only for POST/PUT/PATCH requests'
					/>
				</Form.Item>

				<Row gutter={16}>
					<Col span={12}>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							icon={<PlayCircleOutlined />}
							block
						>
							Execute API Call
						</Button>
					</Col>
					<Col span={12}>
						<Button onClick={handleReset} block>
							Reset Form
						</Button>
					</Col>
				</Row>
			</Form>

			{result && (
				<>
					<Divider>API Response</Divider>
					<Card
						title={
							<Space>
								<Text strong>Result</Text>
								<Tag color={result.success ? 'success' : 'error'}>
									{result.success ? 'SUCCESS' : 'ERROR'}
								</Tag>
							</Space>
						}
						size="small"
					>
						{result.success ? (
							<div>
								<Title level={5}>Response Data:</Title>
								<Card size="small" style={{ backgroundColor: '#f6ffed' }}>
									<pre
										style={{
											margin: 0,
											whiteSpace: 'pre-wrap',
											fontSize: '12px',
											lineHeight: '1.4',
											maxHeight: '400px',
											overflow: 'auto',
										}}
									>
										{(() => {
											try {
												// If it's already a string, try to parse and re-stringify for formatting
												if (typeof result.data === 'string') {
													const parsed = JSON.parse(result.data);
													return JSON.stringify(parsed, null, 2);
												}
												// If it's an object, stringify with formatting
												return JSON.stringify(result.data, null, 2);
											} catch (e) {
												// If parsing fails, return as-is (might be plain text)
												return result.data;
											}
										})()}
									</pre>
								</Card>
							</div>
						) : (
							<div>
								<Title level={5}>Error:</Title>
								<Card size="small" style={{ backgroundColor: '#fff2f0' }}>
									<Text type="danger">{result.error}</Text>
								</Card>
							</div>
						)}

						<Divider />
						<Title level={5}>Request Variables:</Title>
						<Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
							<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
								{JSON.stringify(result.variables, null, 2)}
							</pre>
						</Card>
					</Card>
				</>
			)}

			{loading && (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					<Spin size="large" />
					<div style={{ marginTop: 8 }}>
						<Text>Executing API call...</Text>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default SnappyApiTestModal;
